// resolveHotelFlowAction — pure hotel guided-search state-machine resolver.
//
// Extracted verbatim from `AIChatBot.js` (module-level `resolveHotelFlowAction`)
// as part of the behavior-preserving refactor (issue #240). Given the current
// hotel-search state and the user's text, it returns the next action descriptor
// the stateful handler should perform (start_location / collect_location /
// collect_brand / collect_budget / none). The function performs no state
// mutation and no side effects — it only classifies the input.
//
// Behavior is copied exactly, including the brand/budget option resolution
// (matching by `value` or `label`, falling back to the raw text for brand and to
// `'mid-range'` for budget). As in the source, `userText` is assumed to be a
// string (the caller always passes a trimmed message); the `.toLowerCase()` call
// is preserved verbatim and is not given a defensive guard the source lacked.
//
// React-free and side-effect-free.
//
// Validates: Requirements 3.1, 3.4, 3.5, 1.6

import { BRAND_OPTIONS, BUDGET_OPTIONS } from './hotelFlowOptions';

/**
 * @typedef {Object} HotelFlowAction
 * @property {'start_location'|'collect_location'|'collect_brand'|'collect_budget'|'none'} action
 * @property {string} [brand]
 * @property {string} [brandDisplay]
 * @property {string} [budget]
 */

/**
 * Resolves the next hotel-flow action from the current state and user text.
 *
 * @param {null|'awaiting_location'|'awaiting_brand'|'awaiting_budget'} hotelSearchState
 *   The current hotel-search state-machine value.
 * @param {string} userText - The user's message text.
 * @returns {HotelFlowAction} The action descriptor for the caller to perform.
 */
export function resolveHotelFlowAction(hotelSearchState, userText) {
  const lc = userText.toLowerCase();

  if (hotelSearchState === null && (lc.includes('hotel') || lc === 'hotel')) {
    return { action: 'start_location' };
  }
  if (hotelSearchState === 'awaiting_location') {
    return { action: 'collect_location' };
  }
  if (hotelSearchState === 'awaiting_brand') {
    const brandOpt = BRAND_OPTIONS.find(o => o.value === userText || o.label === userText);
    const brand = brandOpt ? brandOpt.value : userText;
    const brandDisplay = brand === 'no preference' ? 'All brands' : brand;
    return { action: 'collect_brand', brand, brandDisplay };
  }
  if (hotelSearchState === 'awaiting_budget') {
    const budgetOpt = BUDGET_OPTIONS.find(o => o.value === userText || o.label === userText);
    const budget = budgetOpt ? budgetOpt.value : 'mid-range';
    return { action: 'collect_budget', budget };
  }
  return { action: 'none' };
}

export default resolveHotelFlowAction;
